import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Searches by product title across the entire collection (not just the page(s) already
 * loaded on screen). Shopify's collection filters don't support free-text search, so this
 * fetches the collection's public product listing directly and filters client-side.
 *
 * @typedef {{ input: HTMLInputElement, status: HTMLElement, results: HTMLElement }} Refs
 * @extends {Component<Refs>}
 */
class CollectionCardSearch extends Component {
  requiredRefs = ['input', 'status', 'results'];

  /** Guards against a slow, stale request overwriting a newer one. */
  #requestToken = 0;

  /** Caches the full product listing so re-searching doesn't re-download every page. */
  #productsPromise;

  handleInput() {
    this.#debouncedSearch();
  }

  #debouncedSearch = debounce(() => this.#search(), 350);

  async #search() {
    const token = ++this.#requestToken;
    const term = this.refs.input.value.trim();
    const wrapper = this.closest('.collection-wrapper');

    if (!term) {
      wrapper?.classList.remove('collection-wrapper--searching');
      this.refs.status.textContent = '';
      this.refs.results.replaceChildren();
      return;
    }

    this.refs.status.textContent = 'Buscando...';

    let products;
    try {
      this.#productsPromise ??= this.#fetchAllProducts();
      products = await this.#productsPromise;
    } catch {
      this.#productsPromise = undefined;
      if (token !== this.#requestToken) return;
      this.refs.status.textContent = 'No se pudo completar la búsqueda.';
      return;
    }

    if (token !== this.#requestToken) return;

    const termDowncase = term.toLowerCase();
    const matches = products.filter((product) => product.title.toLowerCase().includes(termDowncase));

    wrapper?.classList.add('collection-wrapper--searching');
    this.#renderResults(matches);
    this.refs.status.textContent =
      matches.length === 0
        ? `Sin resultados para "${term}"`
        : `${matches.length} ${matches.length === 1 ? 'resultado' : 'resultados'} para "${term}"`;
  }

  /** Fetches every page of the collection's public product listing. */
  async #fetchAllProducts() {
    const handle = this.dataset.collectionHandle;
    /** @type {any[]} */
    const products = [];

    for (let page = 1; ; page++) {
      const response = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
      if (!response.ok) break;

      /** @type {{ products: any[] }} */
      const data = await response.json();
      if (!data.products || data.products.length === 0) break;

      products.push(...data.products);
      if (data.products.length < 250) break;
    }

    return products;
  }

  /** @param {any[]} products */
  #renderResults(products) {
    const currency = this.dataset.currency;
    const formatter = currency
      ? new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency })
      : null;

    this.refs.results.replaceChildren(
      ...products.map((product) => {
        const li = document.createElement('li');
        li.className = 'collection-card-search__result';

        const link = document.createElement('a');
        link.href = `/products/${product.handle}`;

        const image = document.createElement('img');
        image.className = 'collection-card-search__result-image';
        image.loading = 'lazy';
        image.alt = product.title;
        if (product.images?.[0]?.src) image.src = product.images[0].src;

        const title = document.createElement('p');
        title.className = 'collection-card-search__result-title';
        title.textContent = product.title;

        const price = document.createElement('p');
        price.className = 'collection-card-search__result-price';
        const rawPrice = product.variants?.[0]?.price;
        price.textContent = formatter && rawPrice ? formatter.format(Number(rawPrice)) : (rawPrice ?? '');

        link.append(image, title, price);
        li.append(link);
        return li;
      })
    );
  }
}

if (!customElements.get('collection-card-search')) {
  customElements.define('collection-card-search', CollectionCardSearch);
}
